/**
 * Custom Patterns API calls
 */

import apiClient from './client';
import {
  CustomPattern,
  CustomPatternWithStats,
  CreatePatternRequest,
  UpdatePatternRequest,
  LearnFromSelectionRequest,
  LearnFromSelectionResponse,
  TestPatternRequest,
  TestPatternResponse,
} from '@/types/api';

// ============================================================================
// Pattern CRUD Operations
// ============================================================================

/**
 * Get all custom patterns with statistics
 */
export const getPatterns = async (params?: {
  pattern_type?: string;
  is_active?: boolean;
  library_id?: number;
  skip?: number;
  limit?: number;
}): Promise<CustomPatternWithStats[]> => {
  const response = await apiClient.get<CustomPatternWithStats[]>('/patterns', { params });
  return response.data;
};

/**
 * Get single pattern by ID with statistics
 */
export const getPattern = async (id: number): Promise<CustomPatternWithStats> => {
  const response = await apiClient.get<CustomPatternWithStats>(`/patterns/${id}`);
  return response.data;
};

/**
 * Create new custom pattern
 */
export const createPattern = async (data: CreatePatternRequest): Promise<CustomPattern> => {
  const response = await apiClient.post<CustomPattern>('/patterns', data);
  return response.data;
};

/**
 * Update existing pattern
 */
export const updatePattern = async (
  id: number,
  data: UpdatePatternRequest
): Promise<CustomPattern> => {
  const response = await apiClient.put<CustomPattern>(`/patterns/${id}`, data);
  return response.data;
};

/**
 * Delete pattern
 */
export const deletePattern = async (id: number): Promise<void> => {
  await apiClient.delete(`/patterns/${id}`);
};

// ============================================================================
// Pattern Learning
// ============================================================================

/**
 * Learn a new pattern from text selection
 */
export const learnFromSelection = async (
  data: LearnFromSelectionRequest
): Promise<LearnFromSelectionResponse> => {
  const response = await apiClient.post<LearnFromSelectionResponse>(
    '/patterns/learn-from-selection',
    data
  );
  return response.data;
};

// ============================================================================
// Pattern Testing
// ============================================================================

/**
 * Test a pattern against sample text
 */
export const testPattern = async (data: TestPatternRequest): Promise<TestPatternResponse> => {
  const response = await apiClient.post<TestPatternResponse>('/patterns/test', data);
  return response.data;
};
